/**
 * Typed client for /server-billing/* — backs the Server Billing screen.
 *
 * ENVELOPE: unlike /admin-infra/* (which returns raw shapes), every
 * server-billing controller responds with the standard `{ success, data }`
 * envelope — verified against nurev-api
 * app/controllers/server-billing.js (each handler does
 * `res.json({ success: true, data })`). The legacy dashboard hook
 * (`useServerBillingQuery.js`) read these through an axiosClient that
 * auto-unwraps ONE level, then peeled the envelope a second time in the
 * view (`data?.data?.invoices`, `metrics.data`, etc).
 *
 * The admin `client` does NOT auto-unwrap, so we use `unwrap()` here —
 * it returns `r.data.data`, i.e. the inner payload directly. Field shapes
 * below mirror the DigitalOcean service (app/services/digitalocean.js).
 *
 * Polling cadences match the legacy hook:
 *   - overview: 60s
 *   - forecast: 300s
 *   - invoices: staleTime 300s (no poll)
 *   - invoice detail / metrics: enabled-on-demand
 */
import { client, unwrap } from './client';

// ─── Overview ───────────────────────────────────────────────────────────────

export interface OverviewBalance {
  mtdUsage: number;
  accountBalance: number;
  generatedAt?: string;
}

export type ResourceType = 'droplet' | 'k8s-node-pool' | string;

export interface OverviewResource {
  id: string | number;
  name: string;
  type: ResourceType;
  status: string;
  ip: string;
  spec: string;
  region: string;
  monthlyCost: number;
  createdAt?: string;
}

export interface ServerOverview {
  balance: OverviewBalance;
  resources: OverviewResource[];
  totalResources: number;
  totalMonthlyCost: number;
  activeDroplets: number;
  totalDroplets: number;
  k8sClusters: number;
}

export async function fetchServerOverview(): Promise<ServerOverview> {
  return unwrap<ServerOverview>(
    client.get<{ data: ServerOverview }>('/server-billing/overview')
  );
}

// ─── Forecast ─────────────────────────────────────────────────────────────

export interface ServerForecast {
  mtdUsage: number;
  dailyRate: number;
  projected: number;
  daysElapsed: number;
  daysInMonth: number;
  lastMonthTotal: number;
  // toFixed(1) string when a prior invoice exists, else null.
  monthOverMonth: string | null;
}

export async function fetchServerForecast(): Promise<ServerForecast> {
  return unwrap<ServerForecast>(
    client.get<{ data: ServerForecast }>('/server-billing/forecast')
  );
}

// ─── Invoices (list) ──────────────────────────────────────────────────────
// Raw DigitalOcean billing-history shape (passed through by getInvoices()).

export interface Invoice {
  invoice_uuid: string;
  invoice_period: string;
  amount: string;
  status?: string;
}

export interface InvoicesPayload {
  invoices: Invoice[];
  meta?: { total?: number };
  links?: unknown;
}

export async function fetchServerInvoices(): Promise<InvoicesPayload> {
  return unwrap<InvoicesPayload>(
    client.get<{ data: InvoicesPayload }>('/server-billing/invoices')
  );
}

// ─── Invoice detail (line items) ────────────────────────────────────────────
// Raw DigitalOcean invoice-detail shape (passed through by getInvoiceDetail()).

export interface InvoiceItem {
  product?: string;
  resource_name?: string;
  description?: string;
  amount: string;
  duration?: string;
  duration_unit?: string;
}

export interface InvoiceDetailPayload {
  invoice_items: InvoiceItem[];
  meta?: { total?: number };
}

export async function fetchServerInvoiceDetail(
  uuid: string
): Promise<InvoiceDetailPayload> {
  return unwrap<InvoiceDetailPayload>(
    client.get<{ data: InvoiceDetailPayload }>(
      `/server-billing/invoices/${uuid}`
    )
  );
}

// ─── Droplet metrics ──────────────────────────────────────────────────────
// Each metric is the raw DO monitoring response (Prometheus-style) or null
// when that metric call failed (the controller `.catch(() => null)`s each).

export interface MetricSeries {
  status?: string;
  data?: {
    result?: Array<{
      metric?: Record<string, string>;
      // [unixSeconds, "value"] tuples
      values?: Array<[number, string]>;
    }>;
  };
}

export interface DropletMetricsPayload {
  dropletId: string;
  cpu: MetricSeries | null;
  memory: MetricSeries | null;
  bandwidth: MetricSeries | null;
  load: MetricSeries | null;
  disk: MetricSeries | null;
}

export async function fetchServerMetrics(
  dropletId: string
): Promise<DropletMetricsPayload> {
  return unwrap<DropletMetricsPayload>(
    client.get<{ data: DropletMetricsPayload }>(
      `/server-billing/metrics/${dropletId}`
    )
  );
}
